import { xor } from 'lodash-es';
import { gql } from '@apollo/client';
import { Grid, Box, Typography } from 'vendor/material';
import {
  Banner,
  Button,
  Checkbox,
  FormSection,
  Link,
  TextField,
} from '@tackle-io/platform-ui';
import {
  AzureVendorMarketplaceAccountFormSectionQuery,
  VendorInput,
  useAzureVendorMarketplaceAccountFormSectionQuery,
  useUpdateAzureVendorMarketplaceAccountFormSectionMutation,
} from 'generated/graphql';
import useStyles from '../../../../Settings/Account/marketplace/VendorConfig.styles';
import MsftCollapsibleFormSection from '../components/MsftCollapsibleFormSection';
import { FormProgress, FormProgressQueryResult } from 'utils/yup-extended';
import { Field, FieldArray, Form, Formik, FormikProps } from 'formik';
import {
  object as yupObject,
  string as yupString,
  array as yupArray,
} from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { useMemo } from 'react';

const STEPS_TO_COMPLETE = [
  'commercial-marketplace',
  'account-verification',
  'identify-partner-center',
  'terms-and-conditions',
];

// Keep the same schema and types as Azure since we're using the same backend endpoint
type AdminContact = {
  _id: string;
  name: string | null | undefined;
  email: string | null | undefined;
};

type Values = {
  adminContacts: AdminContact[];
};

const schema = yupObject().shape({
  adminContacts: yupArray().of(
    yupObject().shape({
      _id: yupString().required(),
      name: yupString().required('Name is required'),
      email: yupString()
        .email('Must be a valid email')
        .required('Email is required'),
    }),
  ),
}) as any;

export const vendorToValues = (
  data?: AzureVendorMarketplaceAccountFormSectionQuery,
): Values => {
  return {
    adminContacts: data?.currentUser?.vendor?.onboarding?.azure?.admin_contacts
      ?.length
      ? data?.currentUser?.vendor?.onboarding?.azure?.admin_contacts.map(
          (item) => {
            return {
              name: item.name || '',
              email: item.email || '',
              _id: uuidv4(),
            };
          },
        )
      : [{ name: '', email: '', _id: uuidv4() }],
  };
};

export const valuesToVendorInput = (
  values: Values,
): Omit<VendorInput, 'id'> => {
  const admin_contacts = values.adminContacts
    .filter((item) => item.name || item.email)
    .map((item) => ({
      name: item.name,
      email: item.email,
    }));
  return {
    onboarding: {
      azure: {
        admin_contacts,
      },
    },
  };
};

export const AZURE_VENDOR_MARKETPLACE_ACCOUNT_FORM_SECTION_QUERY = gql`
  query AzureVendorMarketplaceAccountFormSection {
    currentUser {
      id
      vendor {
        id
        onboarding {
          azure {
            account_setup
            commercial_marketplace
            admin_contacts {
              name
              email
            }
          }
        }
      }
    }
  }
`;

export const AZURE_VENDOR_MARKETPLACE_ACCOUNT_FORM_SECTION_MUTATION = gql`
  mutation UpdateAzureVendorMarketplaceAccountFormSection(
    $updates: VendorInput!
  ) {
    updateVendor(updates: $updates) {
      id
      onboarding {
        azure {
          account_setup
          commercial_marketplace
          admin_contacts {
            name
            email
          }
        }
      }
    }
  }
`;

const calculateProgress = (
  data: AzureVendorMarketplaceAccountFormSectionQuery,
): FormProgress => {
  const validAdminContacts =
    data?.currentUser?.vendor?.onboarding?.azure?.admin_contacts?.some(
      (item) => item.name && item.email,
    );

  const account_setup =
    data?.currentUser?.vendor?.onboarding?.azure?.account_setup || [];

  const commercial_marketplace =
    data?.currentUser?.vendor?.onboarding?.azure?.commercial_marketplace || [];
  let complete = 0;

  if (validAdminContacts) {
    complete = complete += 1;
  }

  account_setup.concat(commercial_marketplace).forEach((step) => {
    if (STEPS_TO_COMPLETE.includes(step)) {
      complete = complete + 1;
    }
  });

  return {
    total: 5,
    complete,
  };
};

const VendorMarketplaceAccountFormSection = ({
  icon,
}: {
  icon: JSX.Element;
}) => {
  const {
    data,
    loading: queryLoading,
    error,
  } = useAzureVendorMarketplaceAccountFormSectionQuery({
    context: { batchKey: 'vendor-onboarding' },
  });
  const [updateValues, { loading: mutationLoading }] =
    useUpdateAzureVendorMarketplaceAccountFormSectionMutation();
  const loading = mutationLoading || queryLoading;
  const disabled = Boolean(loading || error || !data?.currentUser?.vendor);

  const classes = useStyles();

  const { complete, total } = calculateProgress(data);
  const percentComplete = (complete / total) * 100;

  const account_setup =
    data?.currentUser?.vendor?.onboarding?.azure?.account_setup || [];

  const commercial_marketplace =
    data?.currentUser?.vendor?.onboarding?.azure?.commercial_marketplace || [];

  const initialValues = useMemo<Values>(() => vendorToValues(data), [data]);

  return (
    <Formik<Values>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values) => {
        updateValues({
          variables: {
            updates: {
              id: data.currentUser?.vendor?.id,
              ...valuesToVendorInput(values),
            },
          },
        });
      }}
    >
      {(formProps) => {
        return (
          <MsftCollapsibleFormSection
            icon={icon}
            slug="prepare-marketplace-account"
            subTitle="Review your legal info, publisher agreement, enroll in the marketplace program, and provide your cloud admin contacts information."
            title="Prepare your marketplace account"
            percentComplete={percentComplete}
            loading={loading}
            permissions="Owner"
            timeToComplete="30 min"
            disabled={disabled}
          >
            <Grid container>
              {(error || (!queryLoading && !data?.currentUser?.vendor)) && (
                <Grid item>
                  <Box p={3}>
                    <Banner
                      title="Unable to load data"
                      body="Something went wrong. Please refresh and try again or contact support."
                      type="danger"
                    />
                  </Box>
                </Grid>
              )}
              <Form>
                <Grid item>
                  <Box p={3} pb={2} className={classes.interGrid}>
                    <Grid container direction="column">
                      <Grid item>
                        <Checkbox
                          disabled={disabled}
                          className={classes.checkBox}
                          checked={account_setup.includes(
                            'identify-partner-center',
                          )}
                          label={
                            <Typography>
                              <b>Legal info</b>
                            </Typography>
                          }
                          onChange={() =>
                            updateValues({
                              variables: {
                                updates: {
                                  id: data.currentUser.vendor.id,
                                  onboarding: {
                                    azure: {
                                      account_setup: xor(account_setup, [
                                        'identify-partner-center',
                                      ]),
                                      commercial_marketplace:
                                        commercial_marketplace,
                                    },
                                  },
                                },
                              },
                            })
                          }
                        />
                      </Grid>
                      <Grid item className={classes.textGrid}>
                        <Box pl={4}>
                          <Typography>
                            Open{' '}
                            <Link
                              external
                              to="https://partner.microsoft.com/en-us/dashboard/account/management"
                              className={classes.link}
                            >
                              Partner Center
                            </Link>{' '}
                            and select <b>Account Settings</b> (under the gear
                            icon) and verify that the information is complete
                            and correct.
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Grid container direction="column">
                      <Grid item>
                        <Checkbox
                          disabled={disabled}
                          className={classes.checkBox}
                          label={
                            <Typography>
                              <b>Commercial marketplace program</b>
                            </Typography>
                          }
                          checked={account_setup.includes(
                            'commercial-marketplace',
                          )}
                          onChange={() =>
                            updateValues({
                              variables: {
                                updates: {
                                  id: data.currentUser.vendor.id,
                                  onboarding: {
                                    azure: {
                                      account_setup: xor(account_setup, [
                                        'commercial-marketplace',
                                      ]),
                                      commercial_marketplace:
                                        commercial_marketplace,
                                    },
                                  },
                                },
                              },
                            })
                          }
                        />
                      </Grid>
                      <Grid item className={classes.textGrid}>
                        <Box pl={4}>
                          <Typography>
                            Enroll in the{' '}
                            <Link
                              external
                              to="https://partner.microsoft.com/en-us/dashboard/account/v3/settings/programs"
                              className={classes.link}
                            >
                              Commercial Marketplace Program
                            </Link>
                            . If you have previously enrolled, be sure you've
                            accepted the latest agreements and check this box as
                            completed.
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Grid container direction="column">
                      <Grid item>
                        <Checkbox
                          disabled={disabled}
                          className={classes.checkBox}
                          checked={commercial_marketplace.includes(
                            'account-verification',
                          )}
                          label={
                            <Typography>
                              <b>Verify authorization</b>
                            </Typography>
                          }
                          onChange={() =>
                            updateValues({
                              variables: {
                                updates: {
                                  id: data.currentUser.vendor.id,
                                  onboarding: {
                                    azure: {
                                      commercial_marketplace: xor(
                                        commercial_marketplace,
                                        ['account-verification'],
                                      ),
                                      account_setup: account_setup,
                                    },
                                  },
                                },
                              },
                            })
                          }
                        />
                      </Grid>
                      <Grid item className={classes.textGrid}>
                        <Box pl={4}>
                          <Typography>
                            Under{' '}
                            <b>
                              <Link
                                external
                                to="https://partner.microsoft.com/en-us/dashboard/account/v3/organization/legalinfo#mpn"
                                className={classes.link}
                              >
                                Account Settings
                              </Link>{' '}
                              &gt; Legal Business Profile
                            </b>
                            , confirm that the Verification Status is
                            "Authorized." If it is, no further action is needed.
                            If the status is "Pending", select the{' '}
                            <b>Learn More</b> link and follow the steps for
                            email verification.
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Grid container direction="column">
                      <Grid item>
                        <Checkbox
                          disabled={disabled}
                          className={classes.checkBox}
                          checked={commercial_marketplace.includes(
                            'terms-and-conditions',
                          )}
                          label={
                            <Typography>
                              <b>Microsoft Publisher Agreement</b>
                            </Typography>
                          }
                          onChange={() =>
                            updateValues({
                              variables: {
                                updates: {
                                  id: data.currentUser.vendor.id,
                                  onboarding: {
                                    azure: {
                                      account_setup: account_setup,
                                      commercial_marketplace: xor(
                                        commercial_marketplace,
                                        ['terms-and-conditions'],
                                      ),
                                    },
                                  },
                                },
                              },
                            })
                          }
                        />
                      </Grid>
                      <Grid item className={classes.textGrid}>
                        <Box pl={4} mb={2}>
                          <Typography>
                            Review the{' '}
                            <Link
                              external
                              to="https://partner.microsoft.com/en-us/dashboard/account/v3/settings/agreements"
                              className={classes.link}
                            >
                              Microsoft Publisher Agreement
                            </Link>{' '}
                            terms and select <b>Accept</b>.
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <FormSection title={<b>Cloud admin contacts</b>}>
                      <Grid container>
                        <Grid item className={classes.checkboxParentContainer}>
                          <Checkbox
                            disabled={disabled}
                            className={classes.checkBox}
                            checked={data?.currentUser?.vendor?.onboarding?.azure?.admin_contacts?.some(
                              (item) => item.name && item.email,
                            )}
                            label={
                              <Typography
                                component="p"
                                className={classes.text}
                              >
                                Please share your cloud admin contacts for
                                Microsoft commercial marketplace with Tackle.
                                This allows us to swiftly address any connection
                                issues, ensuring your data and transactions
                                remain up-to-date in our platform. Your
                                cooperation will minimize delays in case of
                                disconnections and save you time searching for
                                the contact in the future.
                              </Typography>
                            }
                          />
                        </Grid>
                        <Grid
                          container
                          className={classes.contactsFormArrayContainer}
                        >
                          <FieldArray name="adminContacts">
                            {(arrayHelpers) => {
                              const {
                                values: { adminContacts },
                              } = arrayHelpers.form as FormikProps<Values>;
                              return (
                                <Grid
                                  container
                                  item
                                  xs
                                  spacing={1}
                                  direction="column"
                                  alignItems="flex-start"
                                >
                                  {adminContacts?.map((adminContact, index) => {
                                    return (
                                      <Box
                                        py={1}
                                        width={'100%'}
                                        key={adminContact._id}
                                      >
                                        <Grid container spacing={1}>
                                          <Grid item xs={12} sm={6}>
                                            <Field
                                              name={`adminContacts[${index}].name`}
                                            >
                                              {({ field, meta }) => {
                                                return (
                                                  <TextField
                                                    {...field}
                                                    id={`contactname${index}`}
                                                    disabled={disabled}
                                                    error={meta.error}
                                                    label={
                                                      <>
                                                        Name
                                                        <span
                                                          className={
                                                            classes.requiredLabelAsterisk
                                                          }
                                                        >
                                                          {' '}
                                                          *
                                                        </span>
                                                      </>
                                                    }
                                                  />
                                                );
                                              }}
                                            </Field>
                                          </Grid>
                                          <Grid item xs={12} sm={6}>
                                            <Field
                                              name={`adminContacts[${index}].email`}
                                            >
                                              {({ field, meta }) => {
                                                return (
                                                  <TextField
                                                    {...field}
                                                    id={`email${index}`}
                                                    disabled={disabled}
                                                    error={meta.error}
                                                    label={
                                                      <>
                                                        Email
                                                        <span
                                                          className={
                                                            classes.requiredLabelAsterisk
                                                          }
                                                        >
                                                          {' '}
                                                          *
                                                        </span>
                                                      </>
                                                    }
                                                  />
                                                );
                                              }}
                                            </Field>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    );
                                  })}
                                  <Button
                                    variant="text"
                                    appearance="primary"
                                    disabled={!!error}
                                    onClick={() => {
                                      const newAdminContact: AdminContact = {
                                        name: '',
                                        email: '',
                                        _id: uuidv4(),
                                      };
                                      arrayHelpers.push(newAdminContact);
                                    }}
                                  >
                                    Add contact
                                  </Button>
                                </Grid>
                              );
                            }}
                          </FieldArray>
                        </Grid>
                      </Grid>
                      <Grid container item justifyContent="flex-end">
                        <Button
                          appearance="primary"
                          type="submit"
                          disabled={
                            disabled || !formProps.dirty || !formProps.isValid
                          }
                          loading={loading}
                        >
                          {error ? 'Try Again?' : 'Save'}
                        </Button>
                      </Grid>
                    </FormSection>
                  </Box>
                </Grid>
              </Form>
            </Grid>
          </MsftCollapsibleFormSection>
        );
      }}
    </Formik>
  );
};

export const useVendorMarketplaceAccountFormSectionProgress =
  (): FormProgressQueryResult => {
    const { data, loading, error } =
      useAzureVendorMarketplaceAccountFormSectionQuery({
        context: { batchKey: 'vendor-onboarding' },
      });
    const progress = calculateProgress(data);
    return { progress, loading, didError: Boolean(error) };
  };

export default VendorMarketplaceAccountFormSection;
